import { ColDef } from "ag-grid-enterprise";

import { ProductRenderParams } from "@src/components/MainApp/SharedProductPicker/types";
import {
  ProductPickerProduct,
  ProductPickerProductVariant,
} from "@src/store/slices/productPicker/types";

import { ProductHeaderCheckBox } from "./ProductHeaderCheckBox";
import { ProductListCheckBox } from "./ProductListCheckBox";
import { ProductListHeader } from "./ProductListHeader";
import { ProductListImage } from "./ProductListImage";
import { ProductListImageAndText } from "./ProductListImageAndText";
import { ProductListProductHeader } from "./ProductListProductHeader";
import { ProductListStatus } from "./ProductListStatus";
import { ProductListText } from "./ProductListText";
import { ProductListVariantPrice } from "./ProductListVariantPrice";

const BASE_COLUMN_CONFIG = {
  expandWidth: 30,
  checkboxWidth: 40,
  imageWidth: 40,
  priceWidth: 100,
  statusWidth: 80,
} as const;

interface CreateColumnsOptions {
  cellRendererParams?: ProductRenderParams;
  showToolTipText?: boolean;
  loadVariants?: boolean;
}

function createBaseColumns<T>(options: {
  defaultDef: Partial<ColDef<T>>;
  cellRendererParams?: ProductRenderParams;
  showToolTipText?: boolean;
  isVariant?: boolean;
  loadVariants?: boolean;
}): ColDef<T>[] {
  const {
    defaultDef,
    cellRendererParams,
    showToolTipText,
    isVariant = false,
    loadVariants = false,
  } = options;

  const handleCellClick = (params: any) => {
    if (params.data?.variants?.length > 0) {
      params.node.setExpanded(!params.node.expanded);
    }
  };

  const columns: ColDef<T>[] = [
    // Checkbox column
    {
      ...defaultDef,
      colId: "checkbox",
      width: BASE_COLUMN_CONFIG.checkboxWidth,
      headerComponent: isVariant ? undefined : ProductHeaderCheckBox,
      cellRenderer: ProductListCheckBox,
    },
    // Title column
    {
      ...defaultDef,
      colId: "title",
      cellRendererParams: {
        ...cellRendererParams,
        showToolTipText,
        loadVariants,
      },
      headerComponent: isVariant ? undefined : ProductListProductHeader,
      flex: 1,
      cellRenderer: (params: any) => {
        if (loadVariants && !isVariant) {
          return ProductListImageAndText(params);
        }
        return ProductListText(params);
      },
    },
    // Price column
    {
      ...defaultDef,
      colId: "price",
      headerName: "Price",
      headerComponent: isVariant ? undefined : ProductListHeader,
      width: BASE_COLUMN_CONFIG.priceWidth,
      cellRenderer: ProductListVariantPrice,
      onCellClicked: isVariant ? undefined : handleCellClick,
    },
    // Status column
    {
      ...defaultDef,
      colId: "status",
      headerName: "Status",
      headerComponent: isVariant ? undefined : ProductListHeader,
      headerComponentParams: {
        sx: {
          alignItems: "center",
        },
      },
      width: BASE_COLUMN_CONFIG.statusWidth,
      cellRenderer: isVariant
        ? (params) => {
            const parentProduct = params.context?.parentProduct;
            if (!parentProduct?.status) {
              return null;
            }
            return ProductListStatus({ ...params, data: parentProduct });
          }
        : ProductListStatus,
      onCellClicked: isVariant ? undefined : handleCellClick,
    },
  ];

  // Conditionally splice in image column between checkbox and title
  if (!loadVariants && !isVariant) {
    columns.splice(1, 0, {
      ...defaultDef,
      colId: "image",
      width: BASE_COLUMN_CONFIG.imageWidth,
      headerComponent: ProductListHeader,
      cellRenderer: ProductListImage,
      onCellClicked: handleCellClick,
    });
  }

  return columns;
}

export function createProductColumns(
  options: CreateColumnsOptions = {}
): ColDef<ProductPickerProduct>[] {
  const { cellRendererParams, showToolTipText, loadVariants } = options;

  const defaultDef: Partial<ColDef<ProductPickerProduct>> = {
    cellRendererParams,
    resizable: false,
    suppressHeaderMenuButton: true,
    suppressHeaderContextMenu: true,
    suppressAutoSize: true,
  };

  return createBaseColumns<ProductPickerProduct>({
    defaultDef,
    cellRendererParams,
    showToolTipText,
    isVariant: false,
    loadVariants,
  });
}

export function createVariantColumns(): ColDef<ProductPickerProductVariant>[] {
  const defaultDef: Partial<ColDef<ProductPickerProductVariant>> = {
    resizable: false,
    suppressHeaderMenuButton: true,
    suppressHeaderContextMenu: true,
    suppressAutoSize: true,
  };

  const baseColumns = createBaseColumns<ProductPickerProductVariant>({
    defaultDef,
    showToolTipText: false,
    isVariant: true,
  });

  return [
    baseColumns[0], // Checkbox
    baseColumns[1], // Title (ProductListText)
    baseColumns[2], // Price
    baseColumns[3], // Status
  ];
}
